import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import { backendUrl } from './gameplanUtils';

const ImportCsvDialog = ({ open, onClose, onImported, authToken }) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setResult(null); setLoading(false); };
  const handleClose = () => { reset(); onClose(); };

  const handleDownloadTemplate = async () => {
    try {
      const resp = await fetch(`${backendUrl}/api/gameplan/deals/import-csv/template`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'gameplan_deals_template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a CSV file'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${backendUrl}/api/gameplan/deals/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Import failed');
      setResult(data);
      if (data.imported_count > 0) {
        toast.success(`Imported ${data.imported_count} deal(s)`);
        onImported && onImported();
      }
      if (data.error_count > 0) {
        toast.error(`${data.error_count} row(s) had errors – see details below`);
      }
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle>Import Deals from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-700 mb-1">Required columns:</p>
            <code className="block">client_name, submitted_date, est_loan_amount</code>
            <p className="mt-2">Optional: broker, loan_type, lead_source, status_stage, ur, approved_date, approved_amount, projected_settlement_date, settled_date, notes</p>
            <p className="mt-2">Dates accept <code>YYYY-MM-DD</code> or <code>DD/MM/YYYY</code>.</p>
          </div>

          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-csv-template-btn" className="w-full">
            Download CSV template
          </Button>

          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            data-testid="csv-file-input"
          />

          {result && (
            <div className="rounded-md border border-slate-200 p-3 text-sm space-y-2" data-testid="import-csv-result">
              <p className="font-medium">
                <CheckCircle className="w-4 h-4 inline text-emerald-600 mr-1" />
                Imported: <span className="text-emerald-700">{result.imported_count}</span>
                {result.error_count > 0 && <> • Failed: <span className="text-red-600">{result.error_count}</span></>}
              </p>
              {result.errors && result.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-red-600">
                      Row {err.row} ({err.client_name || '–'}): {err.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="import-csv-close-btn">Close</Button>
          <Button onClick={handleUpload} disabled={!file || loading} data-testid="import-csv-upload-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {loading ? 'Uploading…' : 'Upload & Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ImportCsvDialog };
export default ImportCsvDialog;
